import {
  Badge,
  Button,
  createTableColumn,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  Option,
  type TableColumnDefinition,
  Toolbar,
  tokens,
} from "@fluentui/react-components"
import {
  AddRegular,
  MoreHorizontalRegular,
  PeopleRegular,
  SearchRegular,
} from "@fluentui/react-icons"
import type {
  AdminResetPasswordResponse,
  AdminUser,
  AdminUserPage,
  CreateUserRequest,
  UpdateUserRequest,
  UserStatus,
} from "@rema/shared/schemas/users"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useSession } from "../../auth/session"
import { ConfirmDialog } from "../../components/confirm-dialog"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { PageHeader } from "../../components/page-header"
import { api } from "../../lib/api"
import { usersI18n as i18n } from "./i18n"
import { RolesDrawer } from "./roles-drawer"
import { UserFormDrawer } from "./user-form-drawer"

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: { minWidth: "240px" },
  statusFilter: { minWidth: "160px" },
  spacer: { marginLeft: "auto" },
  roles: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
  resetUrl: { wordBreak: "break-all", fontFamily: tokens.fontFamilyMonospace },
})

const QUERY_KEY = ["admin", "users"] as const

type StatusFilter = UserStatus | "all"

const STATUS_LABEL: Record<UserStatus, string> = {
  active: i18n.status.active,
  locked: i18n.status.locked,
  suspended: i18n.status.suspended,
  deleted: i18n.status.deleted,
}

const STATUS_COLOR: Record<UserStatus, "success" | "warning" | "danger" | "informative"> = {
  active: "success",
  locked: "warning",
  suspended: "danger",
  deleted: "informative",
}

const fetchUsers = (cursor: string | null, q: string, status: StatusFilter) =>
  api.get<AdminUserPage>("/admin/users", {
    query: {
      cursor: cursor ?? undefined,
      q: q.trim() || undefined,
      status: status === "all" ? undefined : status,
    },
  })

export const UsersPage = () => {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { hasScope } = useSession()
  const canWrite = hasScope("users:write")

  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [rolesTarget, setRolesTarget] = useState<AdminUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [resetResult, setResetResult] = useState<AdminResetPasswordResponse | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const createMutation = useMutation({
    mutationFn: (input: CreateUserRequest) => api.post<AdminUser>("/admin/users", input),
    onSuccess: invalidate,
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserRequest }) =>
      api.patch<AdminUser>(`/admin/users/${id}`, input),
    onSuccess: invalidate,
  })
  const rolesMutation = useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      api.put<AdminUser>(`/admin/users/${id}/roles`, { role_ids: roleIds }),
    onSuccess: invalidate,
  })
  const lockMutation = useMutation({
    mutationFn: ({ id, lock }: { id: string; lock: boolean }) =>
      api.post<AdminUser>(`/admin/users/${id}/${lock ? "lock" : "unlock"}`),
    onSuccess: invalidate,
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: invalidate,
  })
  const resetMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<AdminResetPasswordResponse>(`/admin/users/${id}/reset-password`),
    onSuccess: (result) => setResetResult(result),
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const columns = useMemo<TableColumnDefinition<AdminUser>[]>(
    () => [
      createTableColumn<AdminUser>({
        columnId: "username",
        renderHeaderCell: () => i18n.columns.username,
        renderCell: (item) => item.username,
      }),
      createTableColumn<AdminUser>({
        columnId: "identifier",
        renderHeaderCell: () => i18n.columns.identifier,
        renderCell: (item) => item.identifier,
      }),
      createTableColumn<AdminUser>({
        columnId: "email",
        renderHeaderCell: () => i18n.columns.email,
        renderCell: (item) => item.email ?? "—",
      }),
      createTableColumn<AdminUser>({
        columnId: "roles",
        renderHeaderCell: () => i18n.columns.roles,
        renderCell: (item) =>
          item.roles.length === 0 ? (
            i18n.noRoles
          ) : (
            <div className={styles.roles}>
              {item.roles.map((role) => (
                <Badge key={role} appearance="outline" color="brand">
                  {role}
                </Badge>
              ))}
            </div>
          ),
      }),
      createTableColumn<AdminUser>({
        columnId: "status",
        renderHeaderCell: () => i18n.columns.status,
        renderCell: (item) => (
          <Badge appearance="filled" color={STATUS_COLOR[item.status]}>
            {STATUS_LABEL[item.status]}
          </Badge>
        ),
      }),
      createTableColumn<AdminUser>({
        columnId: "mfa",
        renderHeaderCell: () => i18n.columns.mfa,
        renderCell: (item) => (item.mfa_enabled ? i18n.mfa.on : i18n.mfa.off),
      }),
      createTableColumn<AdminUser>({
        columnId: "actions",
        renderHeaderCell: () => "",
        renderCell: (item) =>
          canWrite ? (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  aria-label={i18n.actions.label}
                  icon={<MoreHorizontalRegular />}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setEditing(item)
                      setFormOpen(true)
                    }}
                  >
                    {i18n.actions.edit}
                  </MenuItem>
                  <MenuItem onClick={() => setRolesTarget(item)}>{i18n.actions.roles}</MenuItem>
                  <MenuItem
                    onClick={() =>
                      lockMutation.mutate({ id: item.id, lock: item.status !== "locked" })
                    }
                  >
                    {item.status === "locked" ? i18n.actions.unlock : i18n.actions.lock}
                  </MenuItem>
                  <MenuItem onClick={() => resetMutation.mutate(item.id)}>
                    {i18n.actions.resetPassword}
                  </MenuItem>
                  <MenuItem disabled={item.is_master} onClick={() => setDeleteTarget(item)}>
                    {i18n.actions.delete}
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : null,
      }),
    ],
    [canWrite, lockMutation, resetMutation, styles.roles],
  )

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={i18n.search}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={i18n.search}
        contentBefore={<SearchRegular />}
        aria-label={i18n.search}
      />
      <Dropdown
        className={styles.statusFilter}
        value={status === "all" ? i18n.filterAll : STATUS_LABEL[status]}
        selectedOptions={[status]}
        onOptionSelect={(_, data) => setStatus((data.optionValue as StatusFilter) ?? "all")}
        aria-label={i18n.filterStatus}
      >
        <Option value="all">{i18n.filterAll}</Option>
        <Option value="active">{i18n.status.active}</Option>
        <Option value="locked">{i18n.status.locked}</Option>
        <Option value="suspended">{i18n.status.suspended}</Option>
      </Dropdown>
      {canWrite ? (
        <Button
          className={styles.spacer}
          appearance="primary"
          icon={<AddRegular />}
          onClick={openCreate}
        >
          {i18n.create}
        </Button>
      ) : null}
    </Toolbar>
  )

  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <DataGridPage<AdminUser>
        queryKey={[...QUERY_KEY, search.trim(), status]}
        fetchPage={(cursor) => fetchUsers(cursor, search, status)}
        columns={columns}
        getRowId={(item) => item.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<PeopleRegular />}
            title={i18n.empty.title}
            description={i18n.empty.description}
            action={
              canWrite ? (
                <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                  {i18n.empty.action}
                </Button>
              ) : undefined
            }
          />
        }
      />

      <UserFormDrawer
        open={formOpen}
        user={editing}
        onClose={() => setFormOpen(false)}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <RolesDrawer
        open={rolesTarget !== null}
        user={rolesTarget}
        onClose={() => setRolesTarget(null)}
        onSave={(id, roleIds) => rolesMutation.mutateAsync({ id, roleIds })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={i18n.delete.title}
        message={i18n.delete.message}
        confirmLabel={i18n.delete.confirm}
        danger
        confirming={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await deleteMutation.mutateAsync(deleteTarget.id)
          setDeleteTarget(null)
        }}
      />

      <Dialog
        open={resetResult !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setResetResult(null)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.reset.title}</DialogTitle>
            <DialogContent>
              <p>{i18n.reset.description}</p>
              <p className={styles.resetUrl}>{resetResult?.reset_url}</p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  if (resetResult) void navigator.clipboard?.writeText(resetResult.reset_url)
                }}
              >
                {i18n.reset.copy}
              </Button>
              <Button appearance="secondary" onClick={() => setResetResult(null)}>
                {i18n.reset.close}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
